import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Users, BarChart3, ArrowRight, ArrowLeft, ShieldCheck, Zap } from "lucide-react";
import DemoBanner from "./DemoBanner";

const ENTRIES = [
  {
    key: "employee",
    icon: Users,
    title: "Employee Experience",
    description:
      "Start with the employee side and demonstrate how someone can reach a CALLACHAP chaplain by the available support methods.",
    to: "/care-portal/demo/company-a/employee",
    cta: "Open Employee Demo",
  },
  {
    key: "employer",
    icon: BarChart3,
    title: "Employer Administrator Experience",
    description:
      "See the aggregate, non-identifying utilization information available to an organization after employee engagement.",
    to: "/care-portal/demo/company-a/employer",
    cta: "Open Employer Dashboard",
  },
];

export default function CompanyADemoHub() {
  useEffect(() => {
    document.title = "Company A — Live Demonstration";
  }, []);

  return (
    <main data-testid="company-a-demo-hub" className="bg-white text-slate-900 min-h-screen">
      <DemoBanner
        title="Company A — CCCHAPS Care Portal Demonstration Environment"
        subtitle="Real actions. Kept separate from the live CCCHAPS Care Portal."
      />
      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
        <Link
          to="/care-portal/demo"
          data-testid="company-a-hub-back"
          className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900 mb-6"
        >
          <ArrowLeft className="w-4 h-4" strokeWidth={2.2} /> Back to Demo Environments
        </Link>

        <div className="flex items-center gap-3 mb-3">
          <img src="/callachap-logo.png" alt="CALLACHAP" className="h-10 md:h-12 w-auto" />
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-emerald-50 border border-emerald-200 text-[10px] uppercase tracking-[0.22em] font-semibold text-emerald-800">
            <Zap className="w-3 h-3" strokeWidth={2.4} /> Live Demo
          </span>
        </div>
        <p className="text-xs uppercase tracking-[0.22em] text-[#4A6FA5] font-semibold" data-testid="company-a-hub-eyebrow">
          Company A · Live Functional Demonstration
        </p>
        <h1 className="mt-2 font-heading text-3xl sm:text-4xl lg:text-5xl tracking-tight text-slate-900" data-testid="company-a-hub-heading">
          See How CALLACHAP Works
        </h1>
        <p className="mt-4 text-base sm:text-lg text-slate-600 max-w-2xl leading-relaxed" data-testid="company-a-hub-sub">
          Use this demonstration to walk through the employee support experience and then see how activity appears on the employer side — without exposing private conversation details.
        </p>
        <p className="mt-3 text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold" data-testid="company-a-hub-flow-note">
          Recommended demonstration flow: Employee Experience → Employer Dashboard
        </p>

        <div className="mt-8 rounded-xl p-4 md:p-5 bg-[#F0F4F8] border border-[#4A6FA5]/15 flex items-start gap-3 text-sm text-[#3B597B]" data-testid="company-a-hub-isolation-note">
          <ShieldCheck className="w-5 h-5 shrink-0 mt-0.5" strokeWidth={2.2} aria-hidden="true" />
          <p className="leading-relaxed">
            <span className="font-semibold">Demonstration Environment.</span> Company A is a sample organization used to demonstrate CCCHAPS Care Portal workflows. Activity is isolated from live employee, employer, support-request, and production Care Portal data. Demo information is automatically removed after 30 days and may also be cleared after a presentation.
          </p>
        </div>

        <div className="mt-10 grid md:grid-cols-2 gap-5">
          {ENTRIES.map((e) => (
            <Link
              key={e.key}
              to={e.to}
              data-testid={`company-a-hub-${e.key}`}
              className="group rounded-2xl p-6 md:p-8 bg-white border border-slate-200 shadow-sm hover:shadow-lg hover:border-[#4A6FA5]/60 transition-all"
            >
              <span className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-[#F0F4F8] text-[#4A6FA5] group-hover:bg-[#4A6FA5] group-hover:text-white transition-colors mb-4">
                <e.icon className="w-6 h-6" strokeWidth={2} aria-hidden="true" />
              </span>
              <h2 className="font-heading text-xl md:text-2xl tracking-tight text-slate-900">{e.title}</h2>
              <p className="mt-3 text-sm md:text-base text-slate-600 leading-relaxed">{e.description}</p>
              <span className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-[#4A6FA5] group-hover:text-[#3B597B]">
                {e.cta} <ArrowRight className="w-4 h-4" strokeWidth={2.2} />
              </span>
            </Link>
          ))}
        </div>
      </section>
    </main>
  );
}
